import { useRef, useState, useEffect } from 'react';

/**
 * useTouchKanban — touch drag-and-drop para kanbans em iOS/mobile.
 *
 * iOS Safari não suporta a API HTML5 Drag & Drop (draggable/onDragStart/onDrop).
 * Este hook implementa o equivalente via touch events, que funcionam em todos os
 * dispositivos e browsers (iOS Safari, Chrome iOS, Firefox Mobile, Android Chrome, etc.).
 *
 * Em Android Chrome e desktop, a API HTML5 DnD continua funcionando normalmente;
 * o hook cuida de não interferir com ela.
 *
 * Uso:
 *   const { boardRef, touchDragId, touchOverCol, onCardTouchStart, onBoardTouchEnd, clearTouchOnDragStart } = useTouchKanban(handleDropLogic);
 *
 *   - boardRef: ref para colocar no container do board (necessário para listener não-passivo)
 *   - touchDragId: id do card sendo arrastado por toque (para destacar visualmente)
 *   - touchOverCol: key da coluna sob o dedo (para destacar visualmente)
 *   - onCardTouchStart(e, item): colocar no onTouchStart de cada card
 *   - onBoardTouchEnd(e): colocar no onTouchEnd do board
 *   - clearTouchOnDragStart(): chamar no onDragStart de cada card (evita conflito Android Chrome)
 *
 * handleDropLogic(item, colKey): função que o hook chama quando o dedo solta numa coluna.
 */
export default function useTouchKanban(handleDropLogic) {
  const touchStartRef      = useRef(null);  // { item, x, y }
  const touchDragActiveRef = useRef(false); // threshold de 8px já atingido?
  const boardRef           = useRef(null);

  const [touchDragId,  setTouchDragId]  = useState(null); // id do card em drag touch
  const [touchOverCol, setTouchOverCol] = useState(null); // col key sob o dedo

  // Adiciona listener touchmove não-passivo no board para poder chamar preventDefault()
  // (necessário para bloquear scroll enquanto está arrastando).
  // React 17+ pode registrar touch events de forma passiva em alguns contextos,
  // por isso usamos addEventListener direto com { passive: false }.
  useEffect(() => {
    const el = boardRef.current;
    if (!el) return;

    const onTouchMove = (e) => {
      if (!touchStartRef.current) return;

      const touch = e.touches[0];
      const dx = touch.clientX - touchStartRef.current.x;
      const dy = touch.clientY - touchStartRef.current.y;

      // Aguarda threshold de 8px antes de considerar drag
      // (abaixo disso pode ser só um toque / scroll)
      if (!touchDragActiveRef.current && Math.hypot(dx, dy) < 8) return;

      // A partir daqui é drag: bloqueia scroll e ativa estado visual
      touchDragActiveRef.current = true;
      e.preventDefault();

      setTouchDragId(touchStartRef.current.item?.id ?? null);

      // Descobre qual coluna está sob o dedo
      // elementsFromPoint retorna TODOS os elementos naquela coordenada (mais
      // próximo ao topo primeiro), então encontramos o que tem data-col-key.
      const els = document.elementsFromPoint(touch.clientX, touch.clientY);
      const colEl = els.find(el => el.dataset && el.dataset.colKey);
      setTouchOverCol(colEl ? colEl.dataset.colKey : null);
    };

    el.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', onTouchMove);
  }, []); // eslint-disable-line

  /** Chamar no onTouchStart de cada card */
  const onCardTouchStart = (e, item) => {
    touchStartRef.current = {
      item,
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    touchDragActiveRef.current = false;
  };

  /** Chamar no onTouchEnd do board div */
  const onBoardTouchEnd = (e) => {
    if (!touchDragActiveRef.current || !touchStartRef.current) {
      // Não havia drag ativo — limpa e ignora (era um tap normal)
      touchStartRef.current = null;
      setTouchDragId(null);
      setTouchOverCol(null);
      return;
    }

    const item = touchStartRef.current.item;
    const touch = e.changedTouches[0];

    // Qual coluna está sob onde o dedo saiu?
    const els = document.elementsFromPoint(touch.clientX, touch.clientY);
    const colEl = els.find(el => el.dataset && el.dataset.colKey);
    const col = colEl ? colEl.dataset.colKey : null;

    // Limpa estado
    touchStartRef.current = null;
    touchDragActiveRef.current = false;
    setTouchDragId(null);
    setTouchOverCol(null);

    // Dispara a lógica de drop
    if (col && item) {
      handleDropLogic(item, col);
    }
  };

  /**
   * Chamar no onDragStart de cada card (junto com o handler HTML5 DnD existente).
   * Limpa o estado de touch para que, no Android Chrome (onde AMBOS os eventos
   * disparam), apenas o HTML5 DnD processe o movimento.
   */
  const clearTouchOnDragStart = () => {
    touchStartRef.current = null;
    touchDragActiveRef.current = false;
    setTouchDragId(null);
    setTouchOverCol(null);
  };

  return {
    boardRef,
    touchDragId,
    touchOverCol,
    onCardTouchStart,
    onBoardTouchEnd,
    clearTouchOnDragStart,
  };
}
